import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { Modal, Tabs } from '@arco-design/web-react';
import BccTabs from './BccTabs';
import EmptyState from './EmptyState';
import PageContainer, { EmbeddedPage } from './PageContainer';
import TabTitle from './TabTitle';
import { SettingsHeaderActionsView, SettingsHeaderSlotProvider, type SettingsHeaderActions } from './settingsHeaderSlot';
import { useBccTabUrl, TabRouteProvider } from '../hooks/useBccTabUrl';
import { useCanSee } from '../hooks/usePerm';

const TabPane = Tabs.TabPane;

export interface SettingsTabDef<K extends string = string> {
  key: K;
  /** Tab 文案（短名词短语） */
  label: string;
  icon?: ReactNode;
  /**
   * 该 Tab 的权限码（与菜单码同源）：无码不渲染该 Tab，避免「点进去才知道没权限」。
   * 省略表示不设权限（仅用于无需权限的公共配置）。
   */
  permission?: string | string[];
  /** Tab 内容（配置内容块；列表型内容自带 ProPage，无需套设置卡） */
  render: () => ReactNode;
  /** 深链进入时注入的二级 Tab（内容块内有子 Tab 时用，如工单字段/派单规则共用一个组件） */
  routeTab?: string;
  /**
   * 内容块自身还有一层 Tab、且与外面这层切的是同一组视图时置真
   * （如「工单字段」与「派单规则」共用 `TicketFieldConfig`，其内层 Tab 与外层 Tab 同义）：
   * 嵌入态隐去内容块的内层 Tab 栏，避免两层 Tab 切同一件事。
   */
  hideEmbeddedTabs?: boolean;
}

export interface SettingsLandingProps<K extends string = string> {
  /**
   * 兜底页头名：**设置 Landing 一律取 Registry 的菜单名**（`/{domain}/settings` 都有侧栏条目），
   * 故通常不传；只在地址解析不出菜单名时才有意义（延续 PageContainer 的兜底口径）。
   */
  title?: string;
  titleTip?: ReactNode;
  subTitle?: string;
  tabs: readonly SettingsTabDef<K>[];
  /** 当前角色一个 Tab 都看不到时的空态文案 */
  emptyText: string;
  className?: string;
}

/**
 * 设置 Landing（`/{domain}/settings`）：页头 + 页内 Tab + 按权限码裁剪的内容块。
 *
 * 三件此前每个域各抄一遍的事收在这里：Tab 权限裁剪（`useCanSee`）、`?tab=` 深链（`useBccTabUrl`）、
 * 无可见 Tab 时的空态。新增设置分组只登记一条 `SettingsTabDef`。
 *
 * 内容块用 `<EmbeddedPage>` 包裹：块内的 `PageContainer` 因此不渲染页头——**不要**再用页级 CSS
 * 的 `display:none` 去抹内嵌页头，那会把页头唯一承载模块说明的 `titleTip` 一起抹掉。
 *
 * **保存出口**：各 Tab 的保存/重置由内容块注册到本页吸顶页头（`SettingsHeaderSlotProvider`），
 * 只有「当前激活的 Tab」注册，因此整页始终只有一个保存按钮、且滚到任何位置都在视口内。
 */
export default function SettingsLanding<K extends string = string>({
  title,
  titleTip,
  subTitle,
  tabs,
  emptyText,
  className,
}: SettingsLandingProps<K>) {
  const canSee = useCanSee();
  const visible = tabs.filter((t) => !t.permission || canSee(t.permission));
  // 传给 useBccTabUrl 的 allowed/defaultValue 必须是稳定值：`tabs` 由页面内联书写，每帧都是新数组，
  // 直接把 `visible.map(...)` 传下去会让该 hook 的「URL 回灌」effect 每帧重跑，
  // 在路由尚未提交时用旧 ?tab= 把刚切过去的 Tab 写回来（表现为切不回默认分组）。按 key 签名 memo。
  const keysSignature = visible.map((t) => t.key).join('|');
  const allowedKeys = useMemo(
    () => (keysSignature ? (keysSignature.split('|') as K[]) : []),
    [keysSignature]
  );
  const [tab, setTab] = useBccTabUrl<K>({
    defaultValue: (allowedKeys[0] ?? tabs[0]?.key) as K,
    allowed: allowedKeys,
    clearOnDefault: true,
  });
  const [headerActions, setHeaderActions] = useState<SettingsHeaderActions | null>(null);

  // 注册收敛：内容块每次渲染都会传来新对象，比对身份避免无谓重渲染
  const register = useCallback((next: SettingsHeaderActions | null) => {
    setHeaderActions((prev) => (prev === next ? prev : next));
  }, []);

  // 切 Tab：先清空页头操作（避免上一 Tab 的保存按钮残留指向旧表单），有未保存改动时先确认；
  // 确认后**真的还原表单**（调该 Tab 注册的 onReset）——只切 Tab 不还原，会让「放弃修改」文案不实，
  // 用户切回来时改动仍在、页头又显示未保存，读起来像「刚才明明放弃了」。
  const changeTab = (key: string) => {
    if (key === tab) return;
    if (headerActions?.dirty) {
      Modal.confirm({
        title: '当前分组有未保存的修改',
        content: '切换分组将还原这些修改。',
        okText: '放弃修改并切换',
        cancelText: '留在当前分组',
        onOk: () => {
          headerActions.onReset?.();
          setHeaderActions(null);
          setTab(key);
        },
      });
      return;
    }
    setHeaderActions(null);
    setTab(key);
  };

  return (
    <PageContainer
      title={title}
      titleTip={titleTip}
      subTitle={subTitle}
      extra={headerActions ? <SettingsHeaderActionsView actions={headerActions} /> : undefined}
      className={className ? `bcc-embed-scope ${className}` : 'bcc-embed-scope'}
    >
      {visible.length === 0 ? (
        <EmptyState description={emptyText} />
      ) : (
        <SettingsHeaderSlotProvider register={register}>
          <BccTabs activeTab={tab} onChange={changeTab} lazyload>
            {visible.map((t) => {
              const body = t.render();
              const content = (
                <EmbeddedPage active={t.key === tab}>
                  {t.hideEmbeddedTabs ? <div className="bcc-embed-hide-tabs">{body}</div> : body}
                </EmbeddedPage>
              );
              return (
                <TabPane key={t.key} title={<TabTitle icon={t.icon} label={t.label} />}>
                  {t.routeTab ? <TabRouteProvider tab={t.routeTab}>{content}</TabRouteProvider> : content}
                </TabPane>
              );
            })}
          </BccTabs>
        </SettingsHeaderSlotProvider>
      )}
    </PageContainer>
  );
}
